import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { getConsent } from "../api/consent";
import { toConsentLocale } from "../lib/consentLocale";
import { useAuthStore } from "../store/authStore";

/** Query key of the signed-in account's consent overview (with the gate topics' answers). */
export const accountConsentKey = (userId: string | null, locale: string) =>
  ["consent", "account", userId, locale] as const;

/**
 * GET /consent as the signed-in user: the gate state (consentPending), the re-consent topics and the "updated"
 * banner. A separate key from the storage banner's anonymous one, so a cached signed-out answer is never used here.
 */
export function useAccountConsent(enabled = true) {
  const { i18n } = useTranslation();
  const locale = toConsentLocale(i18n.language);
  const userId = useAuthStore((s) => s.userId);
  const signedIn = useAuthStore((s) => s.isAuthenticated());
  const key = accountConsentKey(userId, locale);
  const query = useQuery({
    queryKey: key,
    queryFn: () => getConsent(locale),
    enabled: enabled && signedIn,
    staleTime: 5 * 60_000,
    retry: 1,
  });
  return { ...query, key };
}
